/**
 * Pivot Plugin Styles
 *
 * Uses CSS custom properties with plugin-specific overrides
 * falling back to grid-level variables.
 */

@layer tbw-plugins {
  /* ===== Pivot Row Styles ===== */

  .pivot-group-row {
    display: grid;
    grid-template-columns: var(--tbw-column-template);
    font-weight: 600;
    background: var(--tbw-pivot-group-bg, var(--tbw-color-row-alt));
    min-height: var(--tbw-row-height);
    border-bottom: var(--tbw-row-divider);
  }

  @media (hover: hover) {
    .pivot-group-row:hover {
      background: var(--tbw-pivot-group-hover, var(--tbw-color-row-hover));
    }
  }

  .pivot-leaf-row {
    display: grid;
    grid-template-columns: var(--tbw-column-template);
    background: var(--tbw-pivot-leaf-bg, var(--tbw-color-bg));
    min-height: var(--tbw-row-height);
    border-bottom: var(--tbw-row-divider);
  }

  .pivot-grand-total-row {
    display: grid;
    grid-template-columns: var(--tbw-column-template);
    font-weight: 700;
    background: var(--tbw-pivot-grand-total-bg, var(--tbw-color-header-bg));
    min-height: var(--tbw-row-height);
    border-top: 2px solid var(--tbw-color-border-strong);
  }

  .pivot-grand-total-row > .cell {
    display: flex;
    align-items: center;
    padding: var(--tbw-cell-padding);
    border-right: 1px solid var(--tbw-color-border-cell);
    overflow: hidden;
    min-width: 0;
  }

  .pivot-grand-total-row > .cell:last-child {
    border-right: 0;
  }

  /* ===== Sticky Grand Total Footer ===== */

  .pivot-grand-total-footer {
    position: sticky;
    bottom: 0;
    z-index: var(--tbw-z-layer-pinned-rows, 20);
    background: var(--tbw-pivot-grand-total-bg, var(--tbw-color-header-bg));
    min-width: fit-content;
  }

  /* Cells within pivot rows */
  .pivot-group-row > .cell,
  .pivot-leaf-row > .cell {
    display: flex;
    align-items: center;
    padding: var(--tbw-cell-padding);
    border-right: 1px solid var(--tbw-color-border-cell);
    overflow: hidden;
    min-width: 0;
  }

  .pivot-group-row > .cell:last-child,
  .pivot-leaf-row > .cell:last-child {
    border-right: 0;
  }

  /* ===== Toggle Button ===== */

  .pivot-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--tbw-pivot-toggle-size, var(--tbw-toggle-size, 1.25em));
    height: var(--tbw-pivot-toggle-size, var(--tbw-toggle-size, 1.25em));
    margin-right: var(--tbw-spacing-sm, 0.375em);
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--tbw-pivot-toggle-color, var(--tbw-color-fg-muted));
    border-radius: var(--tbw-border-radius);
    transition:
      background var(--tbw-transition-duration, 120ms) var(--tbw-transition-ease, ease),
      color var(--tbw-transition-duration, 120ms) var(--tbw-transition-ease, ease);
  }

  .pivot-toggle:hover {
    background: var(--tbw-pivot-toggle-hover-bg, var(--tbw-color-row-hover));
    color: var(--tbw-pivot-toggle-hover-color, var(--tbw-color-fg));
  }

  /* SC 2.5.8: grow the target, not the box, so the pivot row keeps its height. */
  .pivot-toggle {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset-block-start: 50%;
      inset-inline-start: 50%;
      translate: -50% -50%;
      inline-size: var(--tbw-touch-target-min, 24px);
      block-size: var(--tbw-touch-target-min, 24px);
    }
  }

  @media (pointer: coarse) {
    .pivot-toggle {
      min-width: var(--tbw-touch-target-min, 24px);
      min-height: var(--tbw-touch-target-min, 24px);
    }
  }

  .pivot-toggle:focus {
    outline: var(--tbw-focus-outline);
    outline-offset: var(--tbw-focus-outline-offset);
  }

  /* ===== Labels & Counts ===== */

  .pivot-label {
    font-weight: inherit;
  }

  .pivot-count {
    color: var(--tbw-pivot-count-color, var(--tbw-color-fg-muted));
    font-size: 0.9em;
    font-weight: normal;
  }

  .pivot-total-row {
    font-weight: bold;
    border-top: 2px solid var(--tbw-pivot-border, var(--tbw-color-border-strong));
  }

  /* Depth-based styling (for non-custom rendered rows) */
  [data-pivot-depth='1'] {
    --tbw-pivot-depth: 1;
  }
  [data-pivot-depth='2'] {
    --tbw-pivot-depth: 2;
  }
  [data-pivot-depth='3'] {
    --tbw-pivot-depth: 3;
  }
  [data-pivot-depth='4'] {
    --tbw-pivot-depth: 4;
  }

  /* ===== Tool Panel Styles ===== */

  .tbw-pivot-panel {
    display: flex;
    flex-direction: column;
    gap: var(--tbw-panel-padding, var(--tbw-spacing-lg, 0.75rem));
    padding: var(--tbw-panel-padding, var(--tbw-spacing-lg, 0.75rem));
    height: 100%;
    overflow-y: auto;
    font-size: var(--tbw-font-size-sm, 0.8125rem);
  }

  .tbw-pivot-section {
    border: 1px solid var(--tbw-pivot-border, var(--tbw-color-border));
    border-radius: var(--tbw-border-radius);
    background: var(--tbw-pivot-section-bg, var(--tbw-color-bg));
  }

  .tbw-pivot-section-header {
    padding: var(--tbw-button-padding, var(--tbw-spacing-md, 0.5rem) var(--tbw-spacing-lg, 0.75rem));
    font-weight: 600;
    background: var(--tbw-pivot-header-bg, var(--tbw-color-header-bg));
    border-bottom: 1px solid var(--tbw-pivot-border, var(--tbw-color-border));
    border-radius: var(--tbw-border-radius) var(--tbw-border-radius) 0 0;
  }

  .tbw-pivot-section-content {
    padding: var(--tbw-panel-gap, var(--tbw-spacing-md, 0.5rem));
  }

  .tbw-pivot-toggle-wrapper {
    display: flex;
    align-items: center;
  }

  .tbw-pivot-toggle-label {
    display: flex;
    align-items: center;
    gap: var(--tbw-panel-gap, var(--tbw-spacing-md, 0.5rem));
    cursor: pointer;
  }

  .tbw-pivot-toggle-label input {
    width: var(--tbw-icon-size, 1rem);
    height: var(--tbw-icon-size, 1rem);
    cursor: pointer;
  }

  /* ===== Drop Zones ===== */

  .tbw-pivot-drop-zone {
    min-height: 60px;
    padding: var(--tbw-panel-gap, var(--tbw-spacing-md, 0.5rem));
    border: 2px dashed var(--tbw-pivot-drop-border, var(--tbw-color-border));
    border-radius: var(--tbw-border-radius);
    background: var(--tbw-pivot-drop-bg, var(--tbw-color-row-alt));
    display: flex;
    flex-wrap: wrap;
    gap: var(--tbw-spacing-sm, 0.375rem);
    align-content: flex-start;
    transition: all 0.15s ease;
  }

  .tbw-pivot-drop-zone.drag-over {
    border-color: var(--tbw-color-accent);
    background: var(--tbw-pivot-drop-active, var(--tbw-focus-background));
  }

  .tbw-pivot-placeholder {
    color: var(--tbw-color-fg-muted);
    font-style: italic;
    padding: var(--tbw-panel-gap, var(--tbw-spacing-md, 0.5rem));
    text-align: center;
    width: 100%;
  }

  /* ===== Field Chips ===== */

  .tbw-pivot-field-chip {
    display: inline-flex;
    align-items: center;
    gap: var(--tbw-spacing-sm, 0.375rem);
    padding: var(--tbw-button-padding-sm, var(--tbw-spacing-xs, 0.25rem) var(--tbw-spacing-md, 0.5rem));
    background: var(--tbw-pivot-chip-bg, var(--tbw-color-header-bg));
    border: 1px solid var(--tbw-pivot-chip-border, var(--tbw-color-border));
    border-radius: var(--tbw-border-radius);
    cursor: grab;
    font-size: var(--tbw-font-size-xs, 0.75rem);
    transition: all 0.15s ease;
  }

  .tbw-pivot-field-chip:hover {
    background: var(--tbw-pivot-chip-hover, var(--tbw-color-row-hover));
    border-color: var(--tbw-color-accent);
  }

  .tbw-pivot-field-chip.available {
    background: var(--tbw-color-bg);
  }

  .tbw-pivot-field-chip.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .tbw-pivot-chip-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
  }

  .tbw-pivot-chip-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--tbw-icon-size, 1rem);
    height: var(--tbw-icon-size, 1rem);
    padding: 0;
    border: none;
    background: transparent;
    color: var(--tbw-color-fg-muted);
    font-size: var(--tbw-font-size-sm, 0.875rem);
    font-weight: bold;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.15s ease;
  }

  .tbw-pivot-chip-remove:hover {
    background: var(--tbw-pivot-chip-remove-hover-bg, var(--tbw-color-accent));
    color: var(--tbw-pivot-chip-remove-hover-fg, var(--tbw-color-accent-fg));
  }

  /* ===== Chip move menu (WCAG 2.2 SC 2.5.7) ===== */

  .tbw-pivot-chip-menu {
    position: fixed;
    margin: 0;
    display: flex;
    flex-direction: column;
    min-width: 12rem;
    padding: var(--tbw-spacing-xs, 0.25rem);
    background: var(--tbw-color-panel-bg, light-dark(#ffffff, #1f1f1f));
    color: var(--tbw-color-fg, light-dark(#1a1a1a, #f0f0f0));
    border: 1px solid var(--tbw-color-border, light-dark(#d0d0d0, #3a3a3a));
    border-radius: var(--tbw-border-radius, 0.25rem);
    box-shadow: var(--tbw-shadow-md, 0 4px 12px rgb(0 0 0 / 25%));
    font: inherit;
    z-index: 10001;

    &[hidden] {
      display: none;
    }
  }

  .tbw-pivot-chip-menu-item {
    min-height: var(--tbw-touch-target-min, 24px);
    padding: var(--tbw-spacing-xs, 0.25rem) var(--tbw-spacing-sm, 0.5rem);
    text-align: start;
    background: none;
    border: none;
    /* Themes set radii for much larger surfaces (Material uses 12px), which
       turns a 24px row into a lozenge. */
    border-radius: min(var(--tbw-border-radius, 0.25rem), 6px);
    color: inherit;
    font: inherit;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: var(--tbw-color-row-hover, light-dark(#f0f0f0, #2a2a2a));
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  /* ===== Value Chips ===== */

  .tbw-pivot-value-chip {
    /* Keep inline-flex from parent, just adjust padding */
    padding: var(--tbw-button-padding-sm, var(--tbw-spacing-xs, 0.25rem) var(--tbw-spacing-md, 0.5rem));
  }

  .tbw-pivot-value-label-wrapper {
    display: flex;
    align-items: center;
    gap: var(--tbw-panel-gap, var(--tbw-spacing-md, 0.5rem));
    flex: 1;
    min-width: 0;
  }

  .tbw-pivot-agg-select {
    padding: var(--tbw-spacing-xs, 0.125rem) var(--tbw-spacing-xs, 0.25rem);
    font-size: var(--tbw-font-size-xs, 0.6875rem);
    border: 1px solid var(--tbw-color-border);
    border-radius: var(--tbw-border-radius);
    background: var(--tbw-color-bg);
    cursor: pointer;
  }

  /* ===== Available Fields ===== */

  .tbw-pivot-available-fields {
    display: flex;
    flex-direction: column;
    gap: var(--tbw-spacing-sm, 0.375rem);
    min-height: 40px;
  }

  .tbw-pivot-field-search {
    width: 100%;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--tbw-color-border, #d0d5dd);
    border-radius: var(--tbw-radius-sm, 0.25rem);
    font-size: var(--tbw-font-size-sm, 0.875rem);
    background: var(--tbw-color-bg, #fff);
    color: var(--tbw-color-fg, #1a1a2e);
    outline: none;
  }

  .tbw-pivot-field-search:focus {
    border-color: var(--tbw-color-primary, #3b82f6);
  }

  .tbw-pivot-fields-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--tbw-spacing-sm, 0.375rem);
  }

  /* ===== Options Panel ===== */

  .tbw-pivot-options {
    display: flex;
    flex-direction: column;
    gap: var(--tbw-panel-gap, var(--tbw-spacing-md, 0.5rem));
  }

  .tbw-pivot-checkbox {
    display: flex;
    align-items: center;
    gap: var(--tbw-panel-gap, var(--tbw-spacing-md, 0.5rem));
    cursor: pointer;
  }

  .tbw-pivot-checkbox input {
    width: var(--tbw-icon-size-sm, 0.875rem);
    height: var(--tbw-icon-size-sm, 0.875rem);
    cursor: pointer;
  }

  /* ===== Expand/Collapse Animations ===== */

  /* Slide animation for rows appearing */
  .pivot-group-row.tbw-pivot-slide-in,
  .pivot-leaf-row.tbw-pivot-slide-in {
    animation: tbw-pivot-slide-in var(--tbw-animation-duration, 200ms) var(--tbw-animation-easing, ease-out) forwards;
  }

  @keyframes tbw-pivot-slide-in {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* Fade animation for rows appearing */
  .pivot-group-row.tbw-pivot-fade-in,
  .pivot-leaf-row.tbw-pivot-fade-in {
    animation: tbw-pivot-fade-in var(--tbw-animation-duration, 200ms) var(--tbw-animation-easing, ease-out) forwards;
  }

  @keyframes tbw-pivot-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
} /* End @layer tbw-plugins */
